import { forwardRef, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Container from '@mui/material/Container';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    Grid,
    Snackbar,
    Typography,
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import MapSelect from '../dataEntry/MapSelect';
import RaceEntryPlayer from '../dataEntry/RaceEntryPlayer';
import Header from '../headers/Header';
import SubmitButton from '../buttons/SubmitButton';
import ShowScoresButton from '../buttons/ShowScoresButton';
import SkipButton from '../buttons/SkipButton';
import RetireButton from '../buttons/RetireButton';
import ConfirmModal from '../modals/ConfirmModal';
import ScoresModal from '../modals/ScoresModal';
import LoadingShells from '../utilComponents/LoadingShells';
import { getMapList } from '../utils/CourseGen';
import { validateRacePlayerInput } from '../utils/InputValidation';
import { rotateRacers } from '../utils/RaceRotator';
import { isTie } from '../utils/TieHandler';
import {
    abandonGameSession,
    completeGameSession,
    GameSessionApiError,
    getGameSession,
    updateGameSession,
} from '../utils/GameSessionApi';
import {
    applyCharactersToRoster,
    clearLegacyGameplayStorage,
    deriveScoreHistory,
    GAMEPLAY_SESSION_STORAGE_KEY,
    getAuthoritativeRaceCount,
    isGameplayDraftV1,
    isCompleteHeat,
    normalizeDraftPlayer,
    shouldEvaluateTieBreak,
    resolveLineupPlayer,
    resolveLineupPlayers,
} from '../utils/GameplayDraft';
import { readCachedRoster } from '../utils/useRoster';

const Constants = require('../constants/Constants');
const IMAGE_URL = Constants.IMAGE_URL;
const CHARACTERS = Constants.CHARACTERS;

const Alert = forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const defaultRace = {
    id: 0,
    name: 'Select Map',
    url: `${IMAGE_URL}map_names/SelectMap.png`,
    mapImage: 'maps/NoMapSelected.png',
};

function playersForForm(players) {
    return players.map((player) => ({
        ...normalizeDraftPlayer(player),
        place: null,
    }));
}

const GrandPrixRaceEntry = () => {
    const navigate = useNavigate();
    const { id: routeSessionId } = useParams();
    const [session, setSession] = useState(null);
    const [draft, setDraft] = useState(null);
    const [playerData, setPlayerData] = useState([]);
    const [raceData, setRaceData] = useState(defaultRace);
    const [mapImage, setMapImage] = useState(`${IMAGE_URL}${defaultRace.mapImage}`);
    const [showCourses, setShowCourses] = useState(false);
    const [showScoreTable, setShowScoreTable] = useState(false);
    const [raceConfirm, setRaceConfirm] = useState(false);
    const [skipConfirm, setSkipConfirm] = useState(false);
    const [retireConfirm, setRetireConfirm] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [loading, setLoading] = useState(true);
    const [alert, setAlert] = useState({ show: false, messages: [] });

    const hydrate = useCallback((loadedSession) => {
        if (!isGameplayDraftV1(loadedSession.draftState)) {
            throw new Error('This gameplay session uses an unsupported draft format.');
        }
        getAuthoritativeRaceCount(loadedSession.draftState);
        const storedRoster = readCachedRoster();
        const savedRoster = loadedSession.draftState.setup.savedRoster ||
            (storedRoster.length > 0 ? storedRoster : loadedSession.draftState.setup.roster);
        const hydratedDraft = {
            ...loadedSession.draftState,
            setup: {
                ...loadedSession.draftState.setup,
                savedRoster: savedRoster.map(normalizeDraftPlayer),
            },
        };
        setSession({ ...loadedSession, draftState: hydratedDraft });
        setDraft(hydratedDraft);
        setPlayerData(playersForForm(hydratedDraft.currentPlayers));
        clearLegacyGameplayStorage();
        localStorage.setItem(GAMEPLAY_SESSION_STORAGE_KEY, loadedSession.sessionId);
    }, []);

    const reloadLatest = useCallback(async (message) => {
        const latest = await getGameSession(routeSessionId);
        if (latest.status === 'completed' && latest.completedGrandPrixId) {
            localStorage.removeItem(GAMEPLAY_SESSION_STORAGE_KEY);
            navigate(`/stats/grandprix/${latest.completedGrandPrixId}`);
            return latest;
        }
        if (latest.status !== 'active') {
            localStorage.removeItem(GAMEPLAY_SESSION_STORAGE_KEY);
            navigate('/grandprix');
            return latest;
        }
        hydrate(latest);
        setRaceData(defaultRace);
        setMapImage(`${IMAGE_URL}${defaultRace.mapImage}`);
        setShowCourses(false);
        setAlert({ show: true, messages: [message] });
        return latest;
    }, [hydrate, navigate, routeSessionId]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        getGameSession(routeSessionId)
            .then((loaded) => {
                if (cancelled) return;
                if (loaded.status === 'completed' && loaded.completedGrandPrixId) {
                    localStorage.removeItem(GAMEPLAY_SESSION_STORAGE_KEY);
                    navigate(`/stats/grandprix/${loaded.completedGrandPrixId}`);
                    return;
                }
                if (loaded.status !== 'active') {
                    localStorage.removeItem(GAMEPLAY_SESSION_STORAGE_KEY);
                    navigate('/grandprix');
                    return;
                }
                hydrate(loaded);
            })
            .catch((error) => {
                if (!cancelled) {
                    if (error instanceof GameSessionApiError && error.status === 404) {
                        localStorage.removeItem(GAMEPLAY_SESSION_STORAGE_KEY);
                        navigate('/grandprix');
                        return;
                    }
                    setAlert({
                        show: true,
                        messages: [error.message || 'Unable to load the gameplay session.'],
                    });
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };
    }, [hydrate, navigate, routeSessionId]);

    const scheduledRaceCount = draft ? getAuthoritativeRaceCount(draft) : 0;
    const mapList = useMemo(
        () => getMapList(scheduledRaceCount),
        [scheduledRaceCount],
    );
    const scoreHistory = useMemo(
        () => draft ? deriveScoreHistory(draft) : [],
        [draft],
    );
    const scheduledRace = draft && !draft.tieBreak.active
        ? mapList[draft.courseIndex]
        : null;
    const displayedRace = raceData.id !== 0 ? raceData : scheduledRace ? {
        ...scheduledRace,
        url: `${IMAGE_URL}${scheduledRace.url}`,
    } : defaultRace;
    const displayedMapImage = raceData.id !== 0
        ? mapImage
        : scheduledRace
            ? `${IMAGE_URL}${scheduledRace.mapImage}`
            : `${IMAGE_URL}${defaultRace.mapImage}`;
    const mapCounter = (draft?.courseIndex || 0) + 1;
    const completionReady = draft
        ? !draft.tieBreak.active && draft.courseIndex >= scheduledRaceCount
        : false;

    const saveDraft = async (nextDraft) => {
        try {
            const saved = await updateGameSession(session.sessionId, session.version, nextDraft);
            hydrate(saved);
            return saved;
        } catch (error) {
            if (error instanceof GameSessionApiError && error.status === 409) {
                await reloadLatest('This Grand Prix changed in another window. The latest saved state has been loaded.');
                error.conflictReloaded = true;
            }
            throw error;
        }
    };

    const resetRaceSelection = () => {
        setRaceData(defaultRace);
        setMapImage(`${IMAGE_URL}${defaultRace.mapImage}`);
        setShowCourses(false);
    };

    const handleRace = (event) => {
        event.preventDefault();
        setRaceData({
            id: event.target.getAttribute('value'),
            name: event.target.getAttribute('name'),
            url: event.target.getAttribute('src'),
        });
        setMapImage(`${IMAGE_URL}${event.target.getAttribute('mapImage')}`);
        setShowCourses(false);
    };

    const updatePlayerData = async (index, field, value) => {
        if (field !== 'playerId') {
            setPlayerData((previous) => previous.map(
                (player, playerIndex) => playerIndex === index
                    ? { ...player, [field]: value }
                    : player,
            ));
            return;
        }

        const selected = resolveLineupPlayer(draft.setup.savedRoster, value);
        if (!selected) {
            setAlert({ show: true, messages: ['Select a player from the saved roster.'] });
            return;
        }
        const nextPlayers = playerData.map((player, playerIndex) => playerIndex === index
            ? { ...selected, place: player.place }
            : player);
        setPlayerData(nextPlayers);
        setLoading(true);
        try {
            await saveDraft({
                ...draft,
                currentPlayers: nextPlayers.map(normalizeDraftPlayer),
            });
        } catch (error) {
            if (!(error instanceof GameSessionApiError && error.status === 409)) {
                setAlert({
                    show: true,
                    messages: [error.message || 'Unable to save the selected lineup.'],
                });
            }
        } finally {
            setLoading(false);
        }
    };

    const handleSkip = async () => {
        setSkipConfirm(false);
        setLoading(true);
        try {
            const nextDraft = { ...draft, courseIndex: draft.courseIndex + 1 };
            if (shouldEvaluateTieBreak(nextDraft)) {
                const tie = nextDraft.races.length === 0
                    ? {
                        tie: true,
                        winners: nextDraft.setup.roster.map(
                            (player) => String(player.playerId),
                        ),
                    }
                    : isTie(deriveScoreHistory(nextDraft));
                if (tie.tie) {
                    nextDraft.tieBreak = { active: true, playerIds: tie.winners };
                    nextDraft.currentPlayers = resolveLineupPlayers(
                        nextDraft.setup.savedRoster || nextDraft.setup.roster,
                        tie.winners,
                    );
                    await saveDraft(nextDraft);
                    resetRaceSelection();
                    setShowSuccess(true);
                    return;
                }

                nextDraft.tieBreak = { active: false, playerIds: [] };
                await finishSession(nextDraft);
                return;
            }
            await saveDraft(nextDraft);
            resetRaceSelection();
        } catch (error) {
            if (!(error instanceof GameSessionApiError && error.status === 409)) {
                setAlert({ show: true, messages: [error.message || 'Unable to save the skipped course.'] });
            }
        } finally {
            setLoading(false);
        }
    };

    const completeSavedSession = async (savedSession, completedDraft) => {
        const completed = await completeGameSession(
            savedSession.sessionId,
            savedSession.version,
            completedDraft,
        );
        localStorage.removeItem(GAMEPLAY_SESSION_STORAGE_KEY);
        navigate(`/stats/grandprix/${completed.completedGrandPrixId}`);
    };

    const finishSession = async (completedDraft) => {
        const savedSession = await saveDraft(completedDraft);
        await completeSavedSession(savedSession, completedDraft);
    };

    const retryCompletion = async () => {
        setLoading(true);
        try {
            await completeSavedSession(session, draft);
        } catch (error) {
            if (error instanceof GameSessionApiError && error.status === 409) {
                await reloadLatest('This Grand Prix changed before completion. The latest saved state has been loaded.');
            } else {
                setAlert({ show: true, messages: [error.message || 'Unable to complete this Grand Prix.'] });
            }
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async () => {
        const racePlayers = playerData
            .filter((player) => player.place !== null && player.place !== undefined)
            .map((player) => ({
                playerId: String(player.playerId),
                characterId: String(player.characterId),
                place: Number(player.place),
            }));
        const validation = validateRacePlayerInput(racePlayers);
        if (!isCompleteHeat(draft.currentPlayers, racePlayers)) {
            validation.valid = false;
            validation.messages.unshift('Enter each expected racer and place exactly once.');
        }
        const selectedRace = raceData.id !== 0 ? raceData : scheduledRace;
        if (!selectedRace || Number(selectedRace.id) < 1) {
            validation.valid = false;
            validation.messages.unshift('Select a course.');
        }
        if (!validation.valid) {
            setRaceConfirm(false);
            setAlert({ show: true, messages: validation.messages });
            return;
        }

        setRaceConfirm(false);
        setLoading(true);
        try {
            const race = {
                courseId: String(selectedRace.id),
                engineClassId: String(draft.setup.engineClassId),
                racePlayers,
            };
            const updatedRoster = applyCharactersToRoster(draft.setup.roster, racePlayers);
            const updatedSavedRoster = applyCharactersToRoster(
                draft.setup.savedRoster,
                racePlayers,
            );
            const withRace = {
                ...draft,
                setup: {
                    ...draft.setup,
                    roster: updatedRoster,
                    savedRoster: updatedSavedRoster,
                },
                races: [...draft.races, race],
                courseIndex: draft.courseIndex + 1,
            };
            const nextScoreHistory = deriveScoreHistory(withRace);
            const scheduledComplete = shouldEvaluateTieBreak(withRace);

            if (scheduledComplete) {
                const tie = isTie(nextScoreHistory);
                if (tie.tie) {
                    withRace.tieBreak = { active: true, playerIds: tie.winners };
                    withRace.currentPlayers = resolveLineupPlayers(
                        updatedSavedRoster,
                        tie.winners,
                    );
                    await saveDraft(withRace);
                    resetRaceSelection();
                    setShowSuccess(true);
                    return;
                }

                withRace.tieBreak = { active: false, playerIds: [] };
                await finishSession(withRace);
                return;
            }

            const rotated = rotateRacers(
                racePlayers,
                updatedRoster,
                draft.setup.ruleId,
                draft.setup.groupSize,
            );
            withRace.currentPlayers = rotated.newRacers.map(normalizeDraftPlayer);
            withRace.tieBreak = { active: false, playerIds: [] };
            await saveDraft(withRace);
            resetRaceSelection();
            setShowSuccess(true);
        } catch (error) {
            if (error instanceof GameSessionApiError && error.status === 409 && !error.conflictReloaded) {
                await reloadLatest('This Grand Prix changed before completion. The latest saved state has been loaded.');
            } else if (!(error instanceof GameSessionApiError && error.status === 409)) {
                setAlert({ show: true, messages: [error.message || 'Unable to save this race.'] });
            }
        } finally {
            setLoading(false);
        }
    };

    const handleRetire = async () => {
        setRetireConfirm(false);
        setLoading(true);
        try {
            await abandonGameSession(session.sessionId, session.version);
            localStorage.removeItem(GAMEPLAY_SESSION_STORAGE_KEY);
            navigate('/grandprix');
        } catch (error) {
            if (error instanceof GameSessionApiError && error.status === 409) {
                await reloadLatest('This Grand Prix changed in another window and was not abandoned.');
            } else {
                setAlert({ show: true, messages: [error.message || 'Unable to abandon this Grand Prix.'] });
            }
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <Container component="main"><LoadingShells /></Container>;

    if (!draft || !session) {
        return (
            <Container component="main" sx={{ mt: 4 }}>
                <Dialog open={alert.show}>
                    <DialogTitle>Unable to load Grand Prix</DialogTitle>
                    <DialogContent>
                        {alert.messages.map((message) => <DialogContentText key={message}>{message}</DialogContentText>)}
                    </DialogContent>
                    <DialogActions><Button onClick={() => navigate('/grandprix')}>Back</Button></DialogActions>
                </Dialog>
            </Container>
        );
    }

    return (
        <Container component="main" sx={{ mt: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Snackbar open={showSuccess} autoHideDuration={3000} onClose={() => setShowSuccess(false)}>
                <Alert onClose={() => setShowSuccess(false)} severity="success" sx={{ width: '100%' }}>
                    Progress saved.
                </Alert>
            </Snackbar>
            <ConfirmModal open={raceConfirm} buttonText="Confirm" confirmType="Submit this Race" onConfirm={handleSubmit} onCancel={() => setRaceConfirm(false)} />
            <ConfirmModal open={retireConfirm} buttonText="Confirm" confirmType="Abandon the Grand Prix" onConfirm={handleRetire} onCancel={() => setRetireConfirm(false)} />
            <ConfirmModal open={skipConfirm} buttonText="Confirm" confirmType="Skip" onConfirm={handleSkip} onCancel={() => setSkipConfirm(false)} />
            <ScoresModal open={showScoreTable} handleClose={() => setShowScoreTable(false)} scoreHistory={scoreHistory} />
            <Dialog open={alert.show}>
                <DialogTitle>Grand Prix</DialogTitle>
                <DialogContent>
                    {alert.messages.map((message, index) => (
                        <DialogContentText key={`error-message-${index}`}>{index + 1}. {message}</DialogContentText>
                    ))}
                </DialogContent>
                <DialogActions><Button onClick={() => setAlert({ show: false, messages: [] })}>Close</Button></DialogActions>
            </Dialog>

            <Box sx={{ p: 1, mb: 1 }}><Header src={`${IMAGE_URL}headers/GrandPrix.png`} alt="Race Entry Title" /></Box>
            <Box sx={{ bgcolor: 'primary.light', p: .5, mb: 2, width: '300px', textAlign: 'center', borderRadius: 2, border: 3, boxShadow: 6 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: 'inherit' }}>{draft.setup.title}</Typography>
            </Box>
            <Box sx={{ p: 1, bgcolor: 'primary.light', borderRadius: 2, border: 3, width: '200px', boxShadow: 6 }}>
                <img loading="lazy" src={displayedMapImage} style={{ minWidth: '200px', width: '70%' }} alt="Chosen Map" />
            </Box>
            {draft.tieBreak.active && (
                <Alert severity="info" sx={{ mt: 2 }}>Tie-break race: select a course for the tied players.</Alert>
            )}
            {completionReady && (
                <Button variant="contained" sx={{ mt: 3 }} onClick={retryCompletion}>
                    Complete Grand Prix
                </Button>
            )}
            {!completionReady && <Box component="form" onSubmit={(event) => { event.preventDefault(); setRaceConfirm(true); }} noValidate sx={{ mt: mapCounter === 1 ? 3 : 1 }} id="RaceEntryForm">
                <MapSelect showCourses={showCourses} handleRace={handleRace} raceData={displayedRace} toggleCourses={() => setShowCourses((shown) => !shown)} />
                {draft.races.length > 0 ? <ShowScoresButton handleShowScores={() => setShowScoreTable(true)} /> : <Box sx={{ mb: 2 }} />}
                <Box sx={{ flexGrow: 1, p: 2, bgcolor: 'primary.light', borderRadius: 3, border: 2, borderColor: 'black', fontWeight: 'bold' }}>
                    <Grid container spacing={1}>
                        {draft.currentPlayers.map((player, index) => (
                            <Grid container item spacing={1} key={`grid-player-${player.playerId}-${mapCounter}`}>
                                <RaceEntryPlayer
                                    key={`player-${player.playerId}-${mapCounter}`}
                                    id={index}
                                    player={player}
                                    roster={draft.setup.savedRoster}
                                    grandPrixRoster={draft.setup.roster}
                                    characters={CHARACTERS}
                                    numberOfPlayers={draft.currentPlayers.length}
                                    onUpdate={updatePlayerData}
                                />
                                {index < draft.currentPlayers.length - 1 && <Divider sx={{ width: '95%', borderBottomWidth: 2, borderColor: 'black', mt: 1, mx: 'auto' }} />}
                            </Grid>
                        ))}
                    </Grid>
                </Box>
                <SubmitButton />
            </Box>}
            <Grid container spacing={1} justifyContent="center" alignItems="center" sx={{ p: 1 }}>
                {draft.courseIndex < scheduledRaceCount && !draft.tieBreak.active && <Grid item xs={5}><SkipButton handleSkip={() => setSkipConfirm(true)} /></Grid>}
                <Grid item xs={5}><RetireButton handleRetire={() => setRetireConfirm(true)} /></Grid>
            </Grid>
        </Container>
    );
};

export default GrandPrixRaceEntry;
